import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  decideDareApproval,
  getDareApproval,
  getDareSubscription,
  recordDareManualPayment,
  reopenDareApproval,
} from '../../dareApprovals/dareApprovals.service';
import {
  ACTION_LABEL,
  APPROVAL_STATUS_CHIP,
  MANUAL_METHODS,
  REJECTION_REASONS,
  ROLE_LABEL,
  SUBSCRIPTION_STATUS,
  approverLabel,
  formatDateTime,
  formatDay,
  reasonLabel,
} from '../../dareApprovals/dareApproval.constants';
import { resultMessage } from '../../../../../service/apiResult';
import {
  detailFieldSx,
  detailLabelSx,
  detailPageCardSx as pageCardSx,
  detailSectionTitleSx,
  detailValueSx,
} from '../../../../styles/detailStyles';

const Field = ({ label, children, value }) => (
  <Grid size={{ xs: 12, md: 6 }}>
    <Box sx={detailFieldSx}>
      <Typography sx={detailLabelSx}>{label}</Typography>
      {children || <Typography sx={detailValueSx}>{value || '-'}</Typography>}
    </Box>
  </Grid>
);

const statusChip = (map, status) => {
  const chip = map[status] || { label: status || '-', bg: '#F1F1F1', color: '#1C1B1F' };
  return <Chip label={chip.label} size="small" sx={{ backgroundColor: chip.bg, color: chip.color, fontWeight: 600, alignSelf: 'flex-start' }} />;
};

/**
 * EK-02 / EK-03 on User Onboarding Details, for a DARE: its sign-up approval (eKutir decides the
 * ones no Ecosystem Integrator can, the rest are read-only here), the decision history, and its
 * subscription and payments. This used to be the separate DARE Approvals page.
 */
export default function DareApprovalSection({ dareUserId, dareName, notify, onChanged }) {
  const [approval, setApproval] = useState(null);
  const [history, setHistory] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [missing, setMissing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectComment, setRejectComment] = useState('');
  const [paymentReference, setPaymentReference] = useState('');

  const load = useCallback(async () => {
    const [detail, billing] = await Promise.all([getDareApproval(dareUserId), getDareSubscription(dareUserId)]);
    if (detail.success) {
      setApproval(detail.data?.approval || null);
      setHistory(detail.data?.history || []);
      setMissing(false);
    } else {
      setApproval(null);
      setHistory([]);
      setMissing(true);
    }
    setSubscription(billing.success ? billing.data : null);
  }, [dareUserId]);

  useEffect(() => {
    if (dareUserId) load();
  }, [dareUserId, load]);

  const run = async (action, successMessage) => {
    setBusy(true);
    const result = await action();
    setBusy(false);
    if (result.success) {
      notify(successMessage);
      await load();
      onChanged?.();
      return true;
    }
    notify(resultMessage(result, 'The action could not be completed.'), 'error');
    return false;
  };

  const approve = () => run(() => decideDareApproval(dareUserId, { status: 'APPROVED' }), `${dareName || 'DARE'} approved`);

  const confirmReject = async () => {
    const done = await run(
      () => decideDareApproval(dareUserId, { status: 'REJECTED', reasonCode: rejectReason, comment: rejectComment }),
      `${dareName || 'DARE'} rejected`,
    );
    if (done) setRejecting(false);
  };

  const reopen = () => run(() => reopenDareApproval(dareUserId, null), `${dareName || 'DARE'} is pending again`);

  // Recording a cash DARE's payment also approves its pending sign-up (one action for eKutir).
  const recordPayment = async () => {
    const done = await run(
      () => recordDareManualPayment(dareUserId, paymentReference),
      approval?.status === 'PENDING' && approval?.approverType === 'EKUTIR'
        ? 'Payment recorded; the DARE is approved and its plan is active'
        : 'Payment recorded; the plan is active',
    );
    if (done) setPaymentReference('');
  };

  const ekutirOwned = approval?.approverType === 'EKUTIR';
  const pendingManual = (subscription?.payments || []).some(
    (payment) => payment.status === 'PENDING' && MANUAL_METHODS.includes(payment.method),
  );

  return (
    <>
      <Box sx={{ ...pageCardSx, mt: 3 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ justifyContent: 'space-between', alignItems: { sm: 'center' }, mb: 2.5 }}>
          <Typography sx={{ ...detailSectionTitleSx, mb: 0 }}>DARE Approval</Typography>
          {ekutirOwned ? (
            <Stack direction="row" spacing={1}>
              {approval.status === 'PENDING' ? (
                <>
                  <Button variant="contained" disabled={busy} onClick={approve} sx={{ textTransform: 'none', bgcolor: '#4F8506', '&:hover': { bgcolor: '#4F8506' } }}>
                    Approve
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    disabled={busy}
                    onClick={() => { setRejectReason(''); setRejectComment(''); setRejecting(true); }}
                    sx={{ textTransform: 'none' }}
                  >
                    Reject
                  </Button>
                </>
              ) : null}
              {approval.status === 'REJECTED' ? (
                <Button variant="outlined" disabled={busy} onClick={reopen} sx={{ textTransform: 'none' }}>
                  Re-open
                </Button>
              ) : null}
            </Stack>
          ) : null}
        </Stack>

        {missing ? (
          <Typography sx={{ color: '#49454F' }}>This DARE has no sign-up approval on record.</Typography>
        ) : approval ? (
          <>
            <Typography sx={{ fontSize: 14, color: '#49454F', mb: 2 }}>
              A DARE can log in only once approved. eKutir decides DAREs whose country had no Ecosystem
              Integrator, or whose integrator has no console; the others are decided by their integrator.
            </Typography>
            <Grid container spacing={2.25}>
              <Field label="Approval status">{statusChip(APPROVAL_STATUS_CHIP, approval.status)}</Field>
              <Field label="Decided by" value={approverLabel(approval)} />
              <Field label="Requested" value={formatDateTime(approval.requestedAt)} />
              <Field
                label="Decision"
                value={approval.decidedAt
                  ? `${formatDateTime(approval.decidedAt)}${approval.decidedByRole ? ` · ${approval.decidedByName || ''} (${ROLE_LABEL[approval.decidedByRole] || approval.decidedByRole})` : ''}`
                  : '-'}
              />
              {approval.reasonCode || approval.comment ? (
                <Field
                  label="Reason"
                  value={[approval.reasonCode ? reasonLabel(approval.reasonCode) : null, approval.comment].filter(Boolean).join(' · ')}
                />
              ) : null}
            </Grid>

            <Typography sx={{ fontWeight: 600, mt: 3, mb: 1.5 }}>Decision history</Typography>
            {history.length ? (
              <Stack spacing={1.5}>
                {history.map((entry, index) => (
                  <Box key={`${entry.action}-${entry.decidedAt}-${index}`} sx={{ borderLeft: '3px solid #E4E6F1', pl: 1.5 }}>
                    <Typography sx={{ fontWeight: 600 }}>
                      {ACTION_LABEL[entry.action] || entry.action}
                      {entry.reasonCode ? ` · ${reasonLabel(entry.reasonCode)}` : ''}
                    </Typography>
                    <Typography sx={{ fontSize: 14, color: '#49454F' }}>
                      {formatDateTime(entry.decidedAt)}
                      {entry.decidedByRole
                        ? ` · by ${entry.decidedByName || ''}${entry.decidedByName ? ' ' : ''}(${ROLE_LABEL[entry.decidedByRole] || entry.decidedByRole})`
                        : ''}
                    </Typography>
                    {entry.comment ? <Typography sx={{ fontSize: 14 }}>{entry.comment}</Typography> : null}
                  </Box>
                ))}
              </Stack>
            ) : (
              <Typography sx={{ fontSize: 14 }}>No decisions recorded yet.</Typography>
            )}
          </>
        ) : (
          <Typography sx={{ color: '#49454F' }}>Loading…</Typography>
        )}
      </Box>

      <Box sx={{ ...pageCardSx, mt: 3 }}>
        <Typography sx={detailSectionTitleSx}>Billing</Typography>
        {subscription ? (
          <>
            <Typography sx={{ fontSize: 14, color: '#49454F', mb: 2 }}>
              Billing is managed by eKutir only. Ecosystem Integrators do not see it.
            </Typography>
            <Grid container spacing={2.25}>
              <Field label="Plan" value={subscription.planName} />
              <Field
                label="Billing"
                value={`${subscription.billingCycle === 'YEARLY' ? 'Yearly' : 'Monthly'}${subscription.price != null ? ` · ${subscription.price}` : ''}`}
              />
              <Field
                label="Current period"
                value={subscription.periodStart ? `${formatDay(subscription.periodStart)} – ${formatDay(subscription.periodEnd)}` : '-'}
              />
              <Field label="Subscription status">
                {statusChip(SUBSCRIPTION_STATUS, subscription.status)}
                {subscription.status === 'OVERDUE' && subscription.graceEndsAt ? (
                  <Typography sx={{ fontSize: 12, color: '#8A5300' }}>Grace until {formatDay(subscription.graceEndsAt)}</Typography>
                ) : null}
              </Field>
            </Grid>

            <Typography sx={{ fontWeight: 600, mt: 3, mb: 1.5 }}>Payments</Typography>
            {(subscription.payments || []).length ? (
              <Stack spacing={1}>
                {subscription.payments.map((payment) => (
                  <Box key={payment.id} sx={{ borderLeft: '3px solid #E4E6F1', pl: 1.5 }}>
                    <Typography sx={{ fontWeight: 600 }}>
                      {payment.amount != null ? payment.amount : '-'} · {payment.method || 'Unknown method'} · {payment.status}
                    </Typography>
                    <Typography sx={{ fontSize: 14, color: '#49454F' }}>
                      {formatDateTime(payment.createdAt)}
                      {payment.transactionReference ? ` · Ref ${payment.transactionReference}` : ''}
                      {payment.confirmedAt
                        ? ` · Recorded by ${payment.confirmedByName || 'eKutir'} on ${formatDateTime(payment.confirmedAt)}`
                        : ''}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            ) : (
              <Typography sx={{ fontSize: 14 }}>No payments on record.</Typography>
            )}

            {pendingManual ? (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ alignItems: { sm: 'center' }, pt: 2 }}>
                <TextField
                  size="small"
                  label="Receipt / transaction reference (optional)"
                  value={paymentReference}
                  onChange={(e) => setPaymentReference(e.target.value)}
                  sx={{ flex: 1 }}
                  inputProps={{ maxLength: 120 }}
                />
                <Button variant="contained" disabled={busy} onClick={recordPayment} sx={{ textTransform: 'none' }}>
                  {approval?.status === 'PENDING' && ekutirOwned ? 'Record cash payment and approve' : 'Record cash payment'}
                </Button>
              </Stack>
            ) : null}
          </>
        ) : (
          <Typography sx={{ fontSize: 14 }}>This DARE has no subscription on record.</Typography>
        )}
      </Box>

      <Dialog open={rejecting} onClose={() => setRejecting(false)} fullWidth maxWidth="sm">
        <DialogTitle>Reject {dareName || 'DARE'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography sx={{ fontSize: 14, color: '#49454F' }}>
              The DARE sees this reason when it tries to log in. You can re-open the request later.
            </Typography>
            <TextField select label="Reason" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} required>
              {REJECTION_REASONS.map((reason) => (
                <MenuItem key={reason.value} value={reason.value}>{reason.label}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Comment (optional)"
              value={rejectComment}
              onChange={(e) => setRejectComment(e.target.value)}
              multiline
              minRows={2}
              inputProps={{ maxLength: 2000 }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejecting(false)}>Cancel</Button>
          <Button color="error" variant="contained" disabled={!rejectReason || busy} onClick={confirmReject}>
            Reject
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
